import { OutboundManagementComponent } from '../outbound-management/outbound-management.component';
import { OutboundOrderDetailComponent } from '../outbound-management/outbound-order-detail/outbound-order-detail.component';
import { OrderFlowBoardComponent } from './order-flow-board/order-flow-board.component';
import { Component, OnInit } from '@angular/core';
import { WebService, lastValueFrom, NavTabService, RmsPulldownService } from '@services';
import { RmsModule } from '@rms';
import { Column, ComputedColumn, HeaderButton } from '@interfaces';
import { OrdersDataService } from '../../../../services/orders-data.service';
import { OrderFormComponent } from './order-form/order-form.component';
// NOTE: imported as a plain TS class only — opened dynamically via
// pulldownService.open(OrderDetailViewComponent, ...), never placed directly
// in this component's own template, so it does NOT belong in the
// @Component imports array below.
import { OrderDetailViewComponent } from './order-detail-view/order-detail-view.component';
// Same story — 5.2.3's "My Orders" screen, opened dynamically, not templated here.
import { MyOrdersStatusComponent } from './my-orders-status/my-orders-status.component';
import { take } from 'rxjs/operators';
import { PAYMENT_STATUSES } from '@shared/constants/payment-status';

interface SalesOrder {
  OrderID: number;
  CompanyName: string;
  SaleDate: string;
  PickupDate: string | null;
  ProposedPickupDate: string | null;
  ProNumber: string | null;
  FirstName: string | null;
  LastName: string | null;
  TruckerName: string | null;
  WarehouseName: string | null;
  OrderStatusID: number;
  StatusName: string;
  PaymentMethodID: number;
  PaymentMethodName: string;
  PaymentStatusID: number | null;
  PaymentStatusName: string | null;
  PriceApprovalRequired: boolean;
  SentToOutbound: string | null;
  OrderItems: any[];
  TotalBags?: number;
}

@Component({
  selector: 'app-order-management',
  imports: [RmsModule, OutboundManagementComponent, OrderFlowBoardComponent],
  templateUrl: './order-management.component.html',
  styleUrl: './order-management.component.scss'
})
export class OrderManagementComponent implements OnInit {
  QueueView: 'orders' | 'shipping' | 'board' = 'orders';
  QueueFilter: 'all' | 'open' | 'approval' | 'traffic' | 'warehouse' = 'all';
  PickupFilter: 'all' | 'today' | 'tomorrow' = 'all';
  VisibleOrders: SalesOrder[] = [];
  Loading = false;
  IncludeOlderOrders = false;
  ActionError = '';
  filterOrders() {
    const day = new Date(); day.setHours(0,0,0,0);
    if(this.PickupFilter === 'tomorrow') day.setDate(day.getDate()+1);
    const dateKey=[day.getFullYear(),String(day.getMonth()+1).padStart(2,'0'),String(day.getDate()).padStart(2,'0')].join('-');
    this.VisibleOrders=this.OrderList.filter(order=>{
      const status=Number(order.OrderStatusID);
      const matches=this.QueueFilter==='all'
        || (this.QueueFilter==='open' && [1,2,3,4,5,6,8].includes(status))
        || (this.QueueFilter==='approval' && !!order.PriceApprovalRequired)
        || (this.QueueFilter==='traffic' && [4,5].includes(status))
        || (this.QueueFilter==='warehouse' && status===6);
      return matches && (this.PickupFilter==='all' || String(order.PickupDate || order.ProposedPickupDate || '').slice(0,10)===dateKey);
    });
  }
  selectFilter(filter: typeof this.QueueFilter) { this.QueueFilter=filter;this.filterOrders(); }
  async selectQueue(view: typeof this.QueueView) { this.QueueView=view; }
  async refreshOrders() {
    if(this.Loading)return;this.Loading=true;this.ActionError='';
    try {
      const [orders,columns]=await Promise.all([this.GetOrderList(),this.OrderColumns.length?Promise.resolve(this.OrderColumns):this.CreateOrderColumns()]);
      this.OrderColumns=columns;this.OrderList=orders;
    } catch {this.ActionError='Orders could not be loaded. Please refresh to retry.';}
    finally {this.Loading=false;}
  }
  openShipping(order: SalesOrder) {
    const ref=this.pulldownService.open(OutboundOrderDetailComponent,{data:{OrderID:order.OrderID},title:'Order #'+order.OrderID+' · Shipping',width:'85vw',maxWidth:'95vw',top:'0',maxHeight:'97vh'});
    ref.afterClosed().pipe(take(1)).subscribe(()=>this.refreshOrders());
  }
  private _orderList: SalesOrder[] = [];
  openOrdersCount = 0;
  needApprovalCount = 0;
  trafficQueueCount = 0;
  warehouseQueueCount = 0;

  get OrderList(): SalesOrder[] { return this._orderList; }
  set OrderList(orders: SalesOrder[]) {
    this._orderList = orders ?? [];
    this.openOrdersCount = this.needApprovalCount = this.trafficQueueCount = this.warehouseQueueCount = 0;
    for (const order of this._orderList) {
      if ([1, 2, 3, 4, 5, 6, 8].includes(order.OrderStatusID)) this.openOrdersCount++;
      if (order.PriceApprovalRequired) this.needApprovalCount++;
      if (order.OrderStatusID === 4 || order.OrderStatusID === 5) this.trafficQueueCount++;
      if (order.OrderStatusID === 6) this.warehouseQueueCount++;
    }
    this.filterOrders();
  }
  OrderColumns: Column[] = [];
  OrderComputedColumns: ComputedColumn[] = [
    { key: 'Bags', formula: (row: SalesOrder) => row.TotalBags ?? (row.OrderItems ?? []).reduce((sum, i) => sum + (i.NumberOfBags ?? 0), 0) }
  ];

  // NEW (5.2.3): "My Orders" moved here into the toolbar row, next to
  // Reports — replaces the earlier standalone summary-bar button.
  Buttons: HeaderButton[] = [{label:'Refresh',onAction:()=>this.refreshOrders()}];


  constructor(
    private webService: WebService,
    private ordersData: OrdersDataService,
    private navTab: NavTabService,
    private pulldownService: RmsPulldownService
  ) { }

  async ngOnInit(OrderID?: any) {
    await this.refreshOrders();

    if (OrderID) {
      const selectedOrder = this.OrderList.find(o => o.OrderID === OrderID);
      if (selectedOrder) {
        this.openOrderDetail(selectedOrder);
      }
    }
  }

  async GetOrderList(): Promise<SalesOrder[]> {
    const rows=await lastValueFrom(this.ordersData.getSalesOrders({summary:true,recentOnly:!this.IncludeOlderOrders}));
    return (rows??[]).map((row:SalesOrder)=>({...row,TraderName:[row.FirstName,row.LastName].filter(Boolean).join(' '),ShippingAction:[5,6,7,8].includes(row.OrderStatusID)?'Shipping & documents':null}));
  }

  async CreateOrderColumns(): Promise<Column[]> {
    const { OrderStatuses, PaymentMethods } = await this.ordersData.getRefData({ keys: 'ALL' });

    return [
      { key: 'OrderID', label: 'Order #', type: 'number', hidden: false, clickable: true, onActionKey: 'openOrderDetail', onAction: (row) => this.openOrderDetail(row) },
      { key: 'CompanyName', label: 'Customer', hidden: false },
      { key: 'TraderName', label: 'Trader', hidden: false },
      { key: 'WarehouseName', label: 'Warehouse', hidden: false },
      { key: 'Bags', label: 'Bags', hidden: false }, // no type:'number' — same rms-table computed-column bug applies here
      { key: 'PickupDate', label: 'Confirmed Pickup', type: 'date', SearchType: 'date', hidden: false },
      { key: 'ProposedPickupDate', label: 'Requested Pickup', type: 'date', SearchType: 'date', hidden: false },
      {
        key: 'PaymentMethodName', label: 'Payment', hidden: false,
        type: 'select', SearchType: 'select',
        selectOptions: (PaymentMethods ?? []).map((p: any) => ({ label: p.MethodName, value: p.MethodName }))
      },
      {
        key: 'PaymentStatusName', label: 'Payment Status', hidden: false,
        type: 'select', SearchType: 'select',
        dropdownSearchOptions: [...PAYMENT_STATUSES.map(s => ({ label: s.StatusName, value: s.StatusName })), { label: 'Partially Paid', value: 'Partially Paid' }],
        // .replace handles "Partially Paid" - toLowerCase() alone leaves the space,
        // which would split into two CSS classes ("partially" "paid") instead of one.
        cellClass: (row) => row.PaymentStatusName ? 'status-pill status-' + String(row.PaymentStatusName).toLowerCase().replace(/\s+/g, '-') : ''
      },
      {
        key: 'StatusName', label: 'Status', hidden: false,
        type: 'select', SearchType: 'select',
        selectOptions: (OrderStatuses ?? []).map((s: any) => ({ label: s.StatusName, value: s.StatusName }))
      },
      { key: 'ShippingAction', label: 'Shipping', hidden: false, clickable: true, onActionKey: 'openShipping', onAction: (row) => this.openShipping(row) },
      { key: 'TruckerName', label: 'Carrier', hidden: true },
      { key: 'SaleDate', label: 'Order Date', type: 'date', SearchType: 'date', hidden: true },
    ];
  }

  // Draft orders open the shared order-form pulldown for editing. Anything
  // else opens the read-only order-detail-view pulldown instead. Neither
  // hides this screen anymore — both just float on top.
  openOrderDetail(order: SalesOrder) {
    if (order.StatusName === 'Draft') {
      const ref = this.pulldownService.open(OrderFormComponent, {
        data: { OrderID: order.OrderID },
        title: `Edit Order #${order.OrderID}`,
        width: '85vw',
        maxWidth: '95vw',
        top: '0',
        maxHeight: '92vh',
        minHeight: '70vh'
      });

      // FIXED: the pulldown's built-in "×" close button always calls
      // requestClose() with no argument, no matter what our own component's
      // close(...) calls pass — so relying on a truthy result to decide
      // whether to refresh silently missed real saves. Now this always
      // refreshes on close.
      ref.afterClosed().pipe(take(1)).subscribe({
        next: async () => {
          this.OrderList = await this.GetOrderList();
        }
      });
    } else {
      const ref = this.pulldownService.open(OrderDetailViewComponent, {
        data: { OrderID: order.OrderID },
        title: `Order #${order.OrderID}`,
        width: '75vw',
        maxWidth: '95vw',
        top: '0',
        maxHeight: '92vh',
        minHeight: '50vh'
      });

      // Same fix as above.
      ref.afterClosed().pipe(take(1)).subscribe({
        next: async () => {
          this.OrderList = await this.GetOrderList();
        }
      });
    }
  }

  openNewOrder() {
    const ref = this.pulldownService.open(OrderFormComponent, {
      data: { LiveBoard: this.QueueView === 'board' },
      title: 'Create Order',
      width: '85vw',
      maxWidth: '95vw',
      top: '0',
      maxHeight: '92vh',
      minHeight: '70vh'
    });

    // FIXED: this was the one subscription still missed when the other two
    // in this file got the "always refresh" fix earlier — this is the exact
    // one hit by "+ Create Order" → Save Draft → close, which is why that
    // specific flow kept failing even after the other fixes landed.
    ref.afterClosed().pipe(take(1)).subscribe({
      next: async () => {
        this.OrderList = await this.GetOrderList();
      }
    });
  }

  // 5.2.3: opens the "My Orders" screen — a trader's own orders with
  // outbound status, so they can spot anything that hasn't successfully
  // reached Outbound. Read-only, so no refresh-on-close needed here.
  openMyOrdersStatus() {
    this.pulldownService.open(MyOrdersStatusComponent, {
      title: 'My Orders',
      width: '70vw',
      maxWidth: '90vw',
      top: '0',
      maxHeight: '90vh',
      minHeight: '50vh'
    });
  }
}
